/**
 * Frontera de analítica (T7 · KAI-94): qué rutas de `apps/web` alimentan Umami.
 *
 * No coincide con la frontera de `noindex`, y confundirlas es el error fácil:
 * `/apuntate` y `/empezar` son noindex —son puertas para quien llega con la
 * tarjeta en la mano, no queremos que Google las liste— pero son páginas
 * públicas de captación y su embudo es justo lo que hay que medir. Al revés,
 * `/s/[publicId]` es la encuesta que responde el comensal: medirla
 * contradiría el pilar de privacidad, que es la promesa que le hacemos a él y
 * al hostelero.
 *
 * La lista se declara por lo PRIVADO y todo lo demás se mide. Es una denylist,
 * con el coste que eso tiene y conviene no maquillar: olvidar una pública
 * cuesta un dato, pero olvidar una privada cuesta una fuga — el error caro
 * está del lado que queda abierto. Se elige igualmente porque las páginas
 * públicas nacen sin parar (marketing, legales, `/ideas`) y una allowlist
 * desactualizada deja el embudo de T7 ciego sin que nadie se entere, mientras
 * que el conjunto privado es cerrado y cambia poco.
 *
 * Quien acota el riesgo que eso deja abierto NO es esta lista: es la allowlist
 * de query de `url.js`. La distinción no es retórica — se comprobó por las
 * malas. Cuando el evento «captura-email» iba por su cuenta sin pasar por esa
 * allowlist, una ruta no declarada (un 404) sí arrastraba el token. La lista
 * de aquí decide QUÉ se mide; lo que garantiza que un secreto no viaje es que
 * todo salga por `track.js` → `url.js`, sin excepciones. Si algún día vuelve a
 * haber un camino que hable con Umami sin pasar por ahí, esta garantía se cae
 * otra vez.
 *
 * Si aparece una ruta privada nueva, va aquí — y el test de este fichero es el
 * sitio donde consta por qué.
 */
export const PRIVATE_PATH_PREFIXES = Object.freeze([
  // Área autenticada del hostelero.
  "/dashboard",
  // Encuesta del comensal: el dato más sensible del producto.
  "/s",
  // Flujos de identidad (las URLs llevan tokens de un solo uso).
  "/sign-in",
  "/auth",
  // Nada bajo /api es una página. Normalmente ni llega aquí, pero `apps/web`
  // no tiene `not-found.js`: un 404 como `/api/prospects/unsubscrib?token=…`
  // (una letra de menos en un enlace de correo) se pinta dentro del layout
  // raíz y sí llegaría. Que no cuente como pageview.
  "/api",
  // La ruta con la que Next prerenderiza el 404 por defecto. Quita el
  // `<script>` del HTML estático de esa página, y nada más — conviene no
  // pedirle lo que no da: al hidratar, `usePathname()` devuelve la ruta real
  // (`/apuntat` en un enlace mal cortado), que es medible, así que el cliente
  // monta el script igual y cuenta el 404 como pageview. Lo único que impide
  // que ese pageview lleve el token es la allowlist de query. Cortarlo de raíz
  // pide un `not-found.js` propio, que es decisión de producto y no de esta
  // issue.
  "/_not-found",
]);

/**
 * ¿Esta ruta puede enviar pageviews/eventos a Umami?
 *
 * Compara por segmento completo, no por prefijo de cadena: `/s` cubre `/s` y
 * `/s/abc123`, pero nunca `/sitemap.xml` ni `/sign-in` (que va por su cuenta).
 *
 * @param {string} pathname ruta absoluta tal cual la da `usePathname()`.
 * @returns {boolean} `false` ante cualquier entrada que no sea una ruta —
 *   en la duda no se mide, que es el lado seguro para un producto de privacidad.
 */
export function isTrackablePath(pathname) {
  if (typeof pathname !== "string" || !pathname.startsWith("/")) return false;
  return !PRIVATE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}
